import { 
  SimpleGrid, 
  Card, 
  CardHeader, 
  CardBody, 
  Heading, 
  VStack, 
  Box, 
  Text,
  UnorderedList,
  ListItem
} from '@chakra-ui/react';

export interface TopicPerspective {
  topic?: string;
  perspective: string;
}

export interface SectorPerspective {
  sector?: string;
  perspective: string;
}

interface PerspectivesSectionProps {
  topicPerspectives: TopicPerspective[];
  sectorPerspectives: SectorPerspective[];
}

export const PerspectivesSection = ({ 
  topicPerspectives,
  sectorPerspectives 
}: PerspectivesSectionProps) => {
  // Ensure arrays are defined
  const safeTopicPerspectives = topicPerspectives || [];
  const safeSectorPerspectives = sectorPerspectives || [];
  
  if (safeTopicPerspectives.length === 0 && safeSectorPerspectives.length === 0) {
    return null;
  }

  // Group topics by topic name
  const groupedTopics = safeTopicPerspectives.reduce((acc, curr) => {
    const topic = curr.topic || 'Uncategorized';
    if (!acc[topic]) {
      acc[topic] = [];
    }
    acc[topic].push(curr.perspective);
    return acc;
  }, {} as Record<string, string[]>);

  // Group sectors by sector name
  const groupedSectors = safeSectorPerspectives.reduce((acc, curr) => {
    const sector = curr.sector || 'Uncategorized';
    if (!acc[sector]) {
      acc[sector] = [];
    }
    acc[sector].push(curr.perspective);
    return acc;
  }, {} as Record<string, string[]>);
  
  return (
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
      {/* Topic Perspectives */}
      {Object.keys(groupedTopics).length > 0 && (
        <Card>
          <CardHeader>
            <Heading size="md">Topic Perspectives</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              {Object.entries(groupedTopics).map(([topic, perspectives], index) => (
                <Box key={index} data-testid="topic-perspective-item">
                  <Text fontWeight="bold" mb={2}>{topic}</Text>
                  <UnorderedList spacing={2}>
                    {perspectives.map((perspective, pIndex) => (
                      <ListItem key={pIndex}>{perspective}</ListItem>
                    ))}
                  </UnorderedList>
                </Box>
              ))}
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Sector Perspectives */}
      {Object.keys(groupedSectors).length > 0 && (
        <Card>
          <CardHeader>
            <Heading size="md">Sector Perspectives</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              {Object.entries(groupedSectors).map(([sector, perspectives], index) => (
                <Box key={index} data-testid="sector-perspective-item">
                  <Text fontWeight="bold" mb={2}>{sector}</Text>
                  <UnorderedList spacing={2}>
                    {perspectives.map((perspective, pIndex) => (
                      <ListItem key={pIndex}>{perspective}</ListItem>
                    ))}
                  </UnorderedList>
                </Box>
              ))}
            </VStack>
          </CardBody>
        </Card>
      )}
    </SimpleGrid>
  );
}; 